import menuArrowIcon from "@assets/images/menu-arrow-icon.svg";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Remove from "@material-ui/icons/Remove";
import useTheme from "@saleor/hooks/useTheme";
import { createHref } from "@saleor/misc";
import classNames from "classnames";
import React, { useState } from "react";
import SVG from "react-inlinesvg";
import { useLocation } from "react-router-dom";

import { drawerNestedMenuWidth, drawerWidthExpandedMobile } from "./consts";
import { IActiveSubMenu } from "./MenuList";
import { filterMenuStructure } from "./menuStructure";
import { IMenuItem } from "./menuStructure";

const useStyles = makeStyles(
  theme => ({
    menuListNested: {
      background: theme.palette.background.paper,
      height: "100vh",
      position: "absolute",
      right: 0,
      top: 0,
      transition: `right  ${theme.transitions.duration.shorter}ms ease`,
      width: drawerNestedMenuWidth,
      zIndex: -1
    },
    menuListNestedClose: {
      "& svg": {
        fill: theme.palette.primary.main,
        left: 11,
        position: "relative",
        top: 1
      },
      border: `solid 1px #EAEAEA`,
      borderRadius: "100%",
      cursor: "pointer",
      height: 32,
      position: "absolute",
      right: 32,
      top: 35,
      transform: "rotate(180deg)",
      width: 32
    },
    menuListNestedCloseDark: {
      border: `solid 1px #252728`
    },
    menuListNestedHide: {
      opacity: 0
    },
    menuListNestedIcon: {
      "& path": {
        fill: "initial"
      },
      "& svg": { height: 32, position: "relative", top: 7, width: 32 }
    },
    menuListNestedIconDark: {
      "& path": {
        fill: theme.palette.common.white
      }
    },
    menuListNestedItem: {
      "&:hover": {
        "& p": {
          color: theme.palette.primary.main
        }
      },
      display: "block",
      marginBottom: theme.spacing(2),
      padding: "0px 30px",
      textDecoration: "none"
    },
    menuListNestedOpen: {
      [theme.breakpoints.down("sm")]: {
        right: 0,
        width: drawerWidthExpandedMobile,
        zIndex: 2
      },
      right: -drawerNestedMenuWidth,
      width: drawerNestedMenuWidth,
      zIndex: -1
    },
    highlightMenu: {
      color: theme.palette.primary.main
    },
    highlightSubMenu: {
      "& a": {
        color: "#0030FF !important"
      },
      "& svg": {
        fill: "#0030FF !important"
      }
    },
    subHeader: {
      borderBottom: "solid 1px #EAEAEA",
      margin: "30px",
      marginBottom: 39,
      paddingBottom: 22
    },
    subHeaderDark: {
      borderBottom: "solid 1px #252728"
    },
    subHeaderTitle: {
      [theme.breakpoints.up("md")]: {
        paddingLeft: 0
      },
      display: "inline",
      paddingLeft: 10
    },
    subMenu: {
      "& li": {
        listStyle: "none"
      },
      "& ul": {
        paddingLeft: 0
      },
      display: "block",
      marginBottom: theme.spacing(2),
      padding: "0px 30px",
      textDecoration: "none"
    },
    subMenuItems: {
      "& a": {
        background: "#fff",
        color: "#3D3D3D",
        display: "block",
        fontFamily: "Henry Sans",
        position: "relative",
        textDecoration: "none"
      },
      "& svg": {
        fill: "#D9D9D9"
      },
      alignItems: "center",
      justifyContent: "flex-start",
      display: "flex"
    }
  }),
  { name: "MenuNested" }
);

export interface MenuNestedProps {
  activeItem: IActiveSubMenu;
  ariaLabel: string;
  closeSubMenu: ({ isActive, label }: IActiveSubMenu) => void;
  icon: string;
  menuItem: IMenuItem;
  title: string;
  handleSubMenu: (itemLabel: string) => void;
  onMenuItemClick: (url: string, event: React.MouseEvent<any>) => void;
}

const MenuNested: React.FC<MenuNestedProps> = props => {
  const {
    activeItem,
    ariaLabel,
    closeSubMenu,
    icon,
    menuItem,
    onMenuItemClick,
    title
  } = props;
  const classes = useStyles(props);
  const [hoveredChild, setHoveredChild] = useState({
    childLabel: "",
    parentURL: ""
  });

  const menuItems = menuItem.children;
  const { isDark } = useTheme();

  const closeMenu = (menuItemUrl, event) => {
    if (!menuItemUrl?.toString().startsWith("#")) {
      onMenuItemClick(menuItemUrl, event);
      closeSubMenu({
        isActive: false,
        label: null
      });
    }
    event.stopPropagation();
    event.preventDefault();
  };

  const handleMouseOver = e => {
    const url = e.target.href;
    const parts = url?.split("/") ?? "";
    const mainURL = parts[3];
    const childLabel = e.target.textContent;
    setHoveredChild({
      childLabel: childLabel ?? "",
      parentURL: mainURL ?? ""
    });
  };

  const handleMouseLeave = () => {
    setHoveredChild({
      childLabel: "",
      parentURL: ""
    });
  };

  const location = useLocation();
  const currentpath = location.pathname;

  return (
    <>
      <div
        className={classNames(classes.menuListNested, {
          [classes.menuListNestedOpen]:
            activeItem.label === ariaLabel && activeItem.isActive
        })}
      >
        <Typography
          className={classNames(classes.subHeader, {
            [classes.subHeaderDark]: isDark
          })}
          variant="h5"
        >
          <Hidden mdUp>
            <SVG
              className={classNames(classes.menuListNestedIcon, {
                [classes.menuListNestedIconDark]: isDark
              })}
              src={icon}
            />
          </Hidden>
          <div className={classes.subHeaderTitle}>{title}</div>
          <Hidden mdUp>
            <div
              className={classNames(classes.menuListNestedClose, {
                [classes.menuListNestedCloseDark]: isDark
              })}
              data-test={ariaLabel}
              onClick={() =>
                closeSubMenu({
                  isActive: false,
                  label: null
                })
              }
            >
              <SVG src={menuArrowIcon} />
            </div>
          </Hidden>
        </Typography>
        {filterMenuStructure(menuItems).map(item => {
          if (item.children) {
            const isHoveringMainMenu =
              hoveredChild.parentURL === item?.label?.toLowerCase();
            const isActiveMainMenu = Boolean(
              currentpath.indexOf(item?.label?.toLowerCase()) !== -1
            );
            return (
              <div className={classNames(classes.subMenu)} key={item.label}>
                <li>
                  <Typography
                    aria-label={item.ariaLabel}
                    className={classNames({
                      [classes.highlightMenu]:
                        isHoveringMainMenu || isActiveMainMenu
                    })}
                  >
                    {item.label}
                  </Typography>
                  {item.children?.map(({ label, url }, index) => {
                    const isActiveSubMenu = Boolean(
                      currentpath.indexOf(url) !== -1
                    );
                    const isHoveringSubMenu = hoveredChild.childLabel === label;
                    return (
                      <ul key={label}>
                        <SubMenu
                          label={label}
                          url={url}
                          key={index}
                          handleMouseOver={handleMouseOver}
                          isHover={isHoveringSubMenu}
                          handleMouseLeave={handleMouseLeave}
                          isActiveSubMenu={isActiveSubMenu}
                        />
                      </ul>
                    );
                  })}
                </li>
              </div>
            );
          } else {
            return (
              <div key={item.label}>
                <a
                  className={classNames(classes.menuListNestedItem)}
                  href={createHref(item.url)}
                  data-test={ariaLabel}
                  onClick={event => closeMenu(item.url, event)}
                  key={item.label}
                >
                  <Typography aria-label={item.ariaLabel}>
                    {item.label}
                  </Typography>
                </a>
              </div>
            );
          }
        })}
      </div>
    </>
  );
};

interface SubMenuProps {
  label: string;
  url: string;
  handleMouseOver: (event: any) => void;
  isHover: boolean;
  isActiveSubMenu: boolean;
  handleMouseLeave: () => void;
}

const SubMenu: React.FC<SubMenuProps> = props => {
  const {
    label,
    url,
    handleMouseOver,
    isHover,
    handleMouseLeave,
    isActiveSubMenu
  } = props;
  const classes = useStyles(props);
  return (
    <li
      className={classNames(classes.subMenuItems, {
        [classes.highlightSubMenu]: isHover || isActiveSubMenu
      })}
      id={label}
      onMouseOver={e => handleMouseOver(e)}
      onMouseLeave={handleMouseLeave}
    >
      <Remove />
      <a href={url}>{label}</a>
    </li>
  );
};

MenuNested.displayName = "MenuNested";
export default MenuNested;
